import { useMemo } from "react";
import type { UseQueryResult } from "@tanstack/react-query";
import { useLocalization } from "@/context/localization-context";
import { FetchingDot, SectionCard } from "../../components/primitives";
import { TimeSeriesChart, type ChartSeries } from "../../components/time-series-chart";
import { formatCount, formatMs, formatMsTick } from "../../format";
import { useMonitoring } from "../../monitoring-context";
import { HTTP_CLASS_COLORS, NEUTRAL, SERIES, SERIES_LIGHT } from "../../palette";
import type { MetricSeries } from "../../types";
import { QueryBody } from "./bits";
import { SLOW_REQUEST_MS } from "./utils";

const CHART_HEIGHT = 200;

/** Four charts of the board window: volume by status class, failed requests, latency, concurrency. */
export function RequestCharts({ metrics }: { metrics: UseQueryResult<MetricSeries> }) {
  const { t } = useLocalization();
  const { window } = useMonitoring();
  const windowLabel = t(`monitoring.window.long.${window}`);
  const fetching = <FetchingDot active={metrics.isFetching} />;
  const chart = (render: (data: MetricSeries) => JSX.Element) => (
    <QueryBody query={metrics} height={CHART_HEIGHT + 24}>
      {render}
    </QueryBody>
  );

  return (
    <div className="grid min-w-0 gap-4 xl:grid-cols-2">
      <SectionCard
        title={t("monitoring.requests.chart.volume")}
        description={t("monitoring.requests.chart.volumeDescription", { window: windowLabel })}
        actions={fetching}
      >
        {chart((data) => <VolumeChart data={data} />)}
      </SectionCard>
      <SectionCard
        title={t("monitoring.requests.chart.failed")}
        description={t("monitoring.requests.chart.failedDescription", { window: windowLabel })}
        actions={fetching}
      >
        {chart((data) => <FailedChart data={data} />)}
      </SectionCard>
      <SectionCard
        title={t("monitoring.requests.chart.latency")}
        description={t("monitoring.requests.chart.latencyDescription", { window: windowLabel })}
        actions={fetching}
      >
        {chart((data) => <LatencyChart data={data} />)}
      </SectionCard>
      <SectionCard
        title={t("monitoring.requests.chart.inFlight")}
        description={t("monitoring.requests.chart.inFlightDescription", { window: windowLabel })}
        actions={fetching}
      >
        {chart((data) => <InFlightChart data={data} />)}
      </SectionCard>
    </div>
  );
}

function VolumeChart({ data }: { data: MetricSeries }) {
  const { t } = useLocalization();
  const series = useMemo<ChartSeries[]>(
    () =>
      (["s2", "s3", "s4", "s5"] as const).map((key) => ({
        key,
        label: t(`monitoring.requests.class.short.${key}`),
        color: HTTP_CLASS_COLORS[key],
        values: data.counters[`http.${key}`] ?? [],
        type: "bar",
        stackId: "http",
      })),
    [data, t],
  );
  return (
    <TimeSeriesChart
      t={data.t}
      stepMs={data.step_ms}
      window={data.window}
      series={series}
      height={CHART_HEIGHT}
      integer
      format={(v) => formatCount(v)}
      emptyText={t("monitoring.requests.chart.noRequests")}
    />
  );
}

function FailedChart({ data }: { data: MetricSeries }) {
  const { t } = useLocalization();
  const series = useMemo<ChartSeries[]>(
    () =>
      (["s4", "s5"] as const).map((key) => ({
        key,
        label: t(`monitoring.requests.class.short.${key}`),
        color: HTTP_CLASS_COLORS[key],
        values: data.counters[`http.${key}`] ?? [],
        type: "bar",
        stackId: "failed",
      })),
    [data, t],
  );
  return (
    <TimeSeriesChart
      t={data.t}
      stepMs={data.step_ms}
      window={data.window}
      series={series}
      height={CHART_HEIGHT}
      integer
      format={(v) => formatCount(v)}
      emptyText={t("monitoring.requests.chart.noFailed")}
    />
  );
}

function LatencyChart({ data }: { data: MetricSeries }) {
  const { t } = useLocalization();
  const hist = data.hists["http.latency_ms"];
  const series = useMemo<ChartSeries[]>(
    () => [
      { key: "p50", label: t("monitoring.series.p50"), color: SERIES_LIGHT, values: hist?.p50 ?? [] },
      { key: "p95", label: t("monitoring.series.p95"), color: SERIES[0], values: hist?.p95 ?? [] },
      { key: "p99", label: t("monitoring.series.p99"), color: SERIES[2], values: hist?.p99 ?? [], dashed: true },
    ],
    [hist, t],
  );
  return (
    <div className="flex flex-col gap-2">
      <TimeSeriesChart
        t={data.t}
        stepMs={data.step_ms}
        window={data.window}
        series={series}
        height={CHART_HEIGHT}
        format={(v) => formatMs(v)}
        tickFormat={formatMsTick}
        referenceLines={[{ y: SLOW_REQUEST_MS, label: t("monitoring.requests.chart.slowLine") }]}
        emptyText={t("monitoring.requests.chart.noRequests")}
      />
      <p className="text-[11px] text-muted-foreground">{t("monitoring.requests.latencyExcludes")}</p>
    </div>
  );
}

function InFlightChart({ data }: { data: MetricSeries }) {
  const { t } = useLocalization();
  const series = useMemo<ChartSeries[]>(
    () => [
      {
        key: "inFlight",
        label: t("monitoring.requests.chart.inFlightSeries"),
        color: NEUTRAL.strong,
        values: data.maxes["http.in_flight"] ?? [],
        type: "area",
        step: true,
      },
    ],
    [data, t],
  );
  return (
    <TimeSeriesChart
      t={data.t}
      stepMs={data.step_ms}
      window={data.window}
      series={series}
      height={CHART_HEIGHT}
      integer
      format={(v) => formatCount(v)}
      emptyText={t("monitoring.requests.chart.noRequests")}
    />
  );
}
